// src/components/TranscriptWindow.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { useVideoPopup } from './VideoPopupContext';
import {
  findRelevantSegment,
  markQueryMatches,
  markSpeakerLabels,
  passageEnd,
} from '../utils/searchRelevance';
import './TranscriptWindow.css';

/* ─── Helpers ─── */
function timestampToSeconds(ts) {
  if (ts === null || ts === undefined || ts === '') return 0;
  if (typeof ts === 'number') return Number.isFinite(ts) ? ts : 0;

  const str = String(ts).trim();

  // Match HH:MM:SS, MM:SS, or HH:MM:SS.fff
  const tm = str.match(/(?:(\d+):)?(\d+):(\d+(?:\.\d+)?)/);
  if (tm) {
    const h = parseInt(tm[1] || '0', 10) || 0;
    const m = parseInt(tm[2], 10) || 0;
    const s = parseFloat(tm[3]) || 0;
    return h * 3600 + m * 60 + s;
  }

  // Numeric string (e.g. "1500" seconds)
  const nm = str.match(/-?\d+(?:\.\d+)?/);
  if (nm) {
    const n = parseFloat(nm[0]);
    if (Number.isFinite(n)) return n;
  }

  return 0;
}

function formatSeconds(total) {
  const t = Math.max(0, Math.floor(Number(total) || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

// Pulls the first [H:MM:SS] (or [MM:SS]) marker from a chunk's content. The
// transcript_chunks.start_timestamp column is a synthetic fixed-interval offset
// that drifts away from the real video time as the meeting progresses, so the
// inline marker baked into the content is the source of truth.
function extractFirstInlineTimestamp(text) {
  if (!text) return null;
  const m = String(text).match(/\[(\d{1,2}:\d{2}(?::\d{2})?)\]/);
  return m ? m[1] : null;
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Wraps embedded timestamps as clickable spans and highlights the search term.
// When `emphasizeRelevant` is set (the anchor-matched chunk), the passage most
// relevant to the query — the named speaker's turn, or the densest cluster of
// query words — additionally gets a `tw-relevant-passage` band so the exact
// part the search hit on stands out inside the highlighted chunk.
function processContent(content, term, { emphasizeRelevant = false } = {}) {
  let processed = escapeHtml(content);

  if (emphasizeRelevant && term && term.trim()) {
    // Offsets are computed on the escaped text so the wrap can't split an
    // HTML entity; no tags exist yet at this point.
    const segment = findRelevantSegment(processed, term);
    if (segment) {
      const end = passageEnd(processed, segment.index);
      processed =
        processed.slice(0, segment.index) +
        '<span class="tw-relevant-passage">' +
        processed.slice(segment.index, end) +
        '</span>' +
        processed.slice(end);
    }
  }

  // Speaker labels named in the query ("President Koo:") — before timestamps
  // become spans, since the label regex keys off "[H:MM:SS] Label:".
  processed = markSpeakerLabels(processed, term, 'tw-speaker-highlight');

  // Query words + exact phrase (tag-safe: skips already-inserted HTML).
  processed = markQueryMatches(processed, term, 'search-highlight');

  // Match HH:MM:SS or MM:SS embedded in transcript text.
  const timestampRegex = /\b(\d{1,2}:\d{2}:\d{2}|\d{1,2}:\d{2})\b/g;
  processed = processed.replace(timestampRegex, (match) => {
    return `<span class="tw-inline-timestamp" data-timestamp="${match}" role="button" tabindex="0">${match}</span>`;
  });

  return processed;
}

/* ─── Component ─── */
export default function TranscriptWindow({
  meetingId,
  meetingDate,
  meetingTitle,
  searchTerm,
  anchorTime,
  videoUrl,
  isWidget = false,
  parentInfo = null,
  useProxy: useProxyProp,
  onJumpToSummary,
  onClose,
}) {
  const { abbreviation } = useParams();
  const { showPopup } = useVideoPopup();
  const [chunks, setChunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const matchedChunkRef = useRef(null);
  const bodyRef = useRef(null);

  /* Fetch chunks for this meeting */
  useEffect(() => {
    if (!meetingId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        let data = [];

        // transcript_chunks is RLS-protected, so the direct anon-key read
        // returns nothing. For districts the serverless function authorizes
        // on our own site — pausd (public) and suhsd (first-party, gated on
        // the board-watch.com Referer) — route the read through the
        // service-role function, which is the only path that bypasses RLS
        // while still verifying the request came from board-watch.com.
        // useProxy is passed by the district page (derived from the district's
        // is_public/is_first_party config). Fall back to the former hardcoded
        // pausd/suhsd check when the prop isn't supplied, so any un-wired caller
        // keeps its previous behavior exactly.
        const abbr = (abbreviation || '').toLowerCase();
        const useProxy = isWidget || (
          useProxyProp !== undefined ? useProxyProp : (abbr === 'pausd' || abbr === 'suhsd')
        );

        if (useProxy) {
          const res = await fetch('/.netlify/functions/districtData', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              resource: 'transcript_chunks',
              abbreviation,
              meetingId,
              parentOrigin: parentInfo?.origin,
              parentUrl: parentInfo?.url,
            }),
          });

          if (!res.ok) {
            const payload = await res.json().catch(() => ({}));
            throw new Error(payload.error || `Request failed (${res.status})`);
          }
          const payload = await res.json();
          data = payload.chunks || [];
        } else {
          const { data: rows, error: e } = await supabase
            .from('transcript_chunks')
            .select('id, start_timestamp, seconds, content, meeting_id')
            .eq('meeting_id', meetingId)
            .order('start_timestamp', { ascending: true });

          if (e) throw e;
          data = rows || [];
        }

        if (cancelled) return;
        setChunks(data);
      } catch (err) {
        if (!cancelled) {
          console.error('TranscriptWindow fetch error:', err);
          setError(err.message || 'Could not load transcript');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [meetingId, isWidget, abbreviation, parentInfo, useProxyProp]);

  /* Derive each chunk's *actual* start time. The transcript_chunks table has
     two columns that look authoritative — `start_timestamp` and `seconds` —
     but both are constructed at a fixed cadence (chunk_index × 160s) and
     drift several minutes from the real video time. The trustworthy source
     is the first [H:MM:SS] marker baked into the chunk's `content`. We fall
     back to start_timestamp only when no inline marker is present. */
  const processedChunks = useMemo(() => {
    return chunks.map((chunk, idx) => {
      const inlineTs = extractFirstInlineTimestamp(chunk.content);
      const fallbackTs = chunk.start_timestamp;
      const displayTs = inlineTs || fallbackTs || null;
      const startSec = timestampToSeconds(displayTs);
      // Duration is approximated from the next chunk's start (computed below)
      // when needed — it isn't reliable from the `seconds` column either.
      return { ...chunk, _startSec: startSec, _displayTs: displayTs, _idx: idx };
    });
  }, [chunks]);

  /* Compute matched chunk index based on anchorTime. Because chunks are
     sorted by start time, the matched chunk is the last one whose start is
     ≤ anchor — i.e. anchor falls in the [start, next_start) window. */
  const matchedIndex = useMemo(() => {
    if (!processedChunks.length) return -1;
    if (anchorTime === null || anchorTime === undefined || anchorTime === '') return 0;
    const anchorSec = timestampToSeconds(anchorTime);
    if (!Number.isFinite(anchorSec)) return 0;

    let bestIdx = 0;
    let bestDelta = Infinity;
    for (let i = 0; i < processedChunks.length; i++) {
      const startSec = processedChunks[i]._startSec;
      const nextStart = i + 1 < processedChunks.length
        ? processedChunks[i + 1]._startSec
        : startSec + 600; // last chunk gets a 10-minute window

      if (anchorSec >= startSec && anchorSec < nextStart) return i;

      const delta = Math.abs(anchorSec - startSec);
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIdx = i;
      }
    }
    return bestIdx;
  }, [processedChunks, anchorTime]);

  /* Auto-scroll matched chunk into view inside the body container.
     getBoundingClientRect is reliable regardless of which ancestor is positioned. */
  useEffect(() => {
    if (loading || matchedIndex < 0) return;
    const t = setTimeout(() => {
      const target = matchedChunkRef.current;
      const container = bodyRef.current;
      if (!target || !container) return;
      const targetRect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const relativeTop = container.scrollTop + (targetRect.top - containerRect.top);
      const offset = relativeTop - container.clientHeight / 2 + target.clientHeight / 2;
      container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
    }, 80);
    return () => clearTimeout(t);
  }, [loading, matchedIndex, processedChunks.length]);

  /* Click handlers — both side button and inline timestamps end up here. */
  const playAtTimestamp = useCallback(
    (ts, target) => {
      if (!videoUrl) return;
      const label = typeof ts === 'number' ? formatSeconds(ts) : String(ts);
      if (!label) return;
      showPopup(label, videoUrl, target);
    },
    [videoUrl, showPopup]
  );

  const handleSideTimestampClick = (e, ts) => {
    e.stopPropagation();
    playAtTimestamp(ts, e.currentTarget);
  };

  // Delegated click handler for embedded `.tw-inline-timestamp` spans.
  const handleChunksClick = (e) => {
    const target = e.target.closest && e.target.closest('.tw-inline-timestamp');
    if (!target) return;
    e.stopPropagation();
    e.preventDefault();
    const ts = target.getAttribute('data-timestamp');
    if (ts) playAtTimestamp(ts, target);
  };

  const handleJumpClick = (e) => {
    e.stopPropagation();
    if (onJumpToSummary) onJumpToSummary();
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    if (onClose) onClose();
  };

  return (
    <div className="transcript-window" onClick={(e) => e.stopPropagation()}>
      <div className="tw-header">
        <div className="tw-header-info">
          <span className="tw-label">Transcript Context</span>
          {(meetingTitle || meetingDate) && (
            <span className="tw-meta">
              {meetingTitle ? `${meetingTitle}` : ''}
              {meetingTitle && meetingDate ? ' · ' : ''}
              {meetingDate || ''}
            </span>
          )}
        </div>
        <button
          className="tw-close"
          onClick={handleCloseClick}
          aria-label="Close transcript context"
          type="button"
        >
          ×
        </button>
      </div>

      <div className="tw-body" ref={bodyRef}>
        {loading && (
          <div className="tw-status">
            <div className="tw-spinner" />
            <span>Loading transcript…</span>
          </div>
        )}

        {!loading && error && (
          <div className="tw-status tw-error">
            <span>Could not load transcript: {error}</span>
          </div>
        )}

        {!loading && !error && processedChunks.length === 0 && (
          <div className="tw-status tw-empty">
            <span>No transcript chunks are available for this meeting yet.</span>
          </div>
        )}

        {!loading && !error && processedChunks.length > 0 && (
          <ol className="tw-chunks" onClick={handleChunksClick}>
            {processedChunks.map((chunk, idx) => {
              const isMatched = idx === matchedIndex;
              // Prefer the original inline string ("0:36:39") when present so
              // the label matches the timestamps shown elsewhere in the app;
              // fall back to a formatted form only when we had to compute one.
              const tsLabel = chunk._displayTs || formatSeconds(chunk._startSec);
              const tsForClick = chunk._displayTs || chunk._startSec;
              const html = processContent(chunk.content || '', searchTerm, {
                emphasizeRelevant: isMatched,
              });

              return (
                <li
                  key={chunk.id ?? `${idx}-${chunk._startSec}`}
                  ref={isMatched ? matchedChunkRef : null}
                  className={`tw-chunk${isMatched ? ' tw-chunk-matched' : ''}`}
                >
                  <button
                    type="button"
                    className="tw-timestamp"
                    onClick={(e) => handleSideTimestampClick(e, tsForClick)}
                    disabled={!videoUrl}
                    title={videoUrl ? 'Play video at this timestamp' : 'Video link not available'}
                  >
                    {tsLabel}
                  </button>
                  <span
                    className="tw-chunk-content"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="tw-footer">
        <span className="tw-footer-hint">
          {!loading && !error && processedChunks.length > 0
            ? `Showing ${processedChunks.length} transcript ${processedChunks.length === 1 ? 'segment' : 'segments'}`
            : ''}
        </span>
        <button
          type="button"
          className="tw-jump-btn"
          onClick={handleJumpClick}
        >
          View Meeting Summary →
        </button>
      </div>
    </div>
  );
}
